import React, { useState } from "react";
import {
  PhotoIcon,
  CodeBracketIcon,
  CommandLineIcon,
  DocumentTextIcon,
  BeakerIcon,
  ChatBubbleLeftRightIcon,
  PlusIcon,
} from "@heroicons/react/24/outline";

// Evidence list/upload UI — local state only, no real upload backend.
const EVIDENCE_TYPES = [
  { id: "Screenshot", icon: PhotoIcon },
  { id: "HTTP Request/Response", icon: CodeBracketIcon },
  { id: "Terminal Output", icon: CommandLineIcon },
  { id: "Log", icon: DocumentTextIcon },
  { id: "PoC", icon: BeakerIcon },
  { id: "Note", icon: ChatBubbleLeftRightIcon },
];

const iconFor = (type) => (EVIDENCE_TYPES.find((t) => t.id === type) || EVIDENCE_TYPES[3]).icon;

const VaptEvidencePanel = ({ evidence = [], onAdd }) => {
  const [showForm, setShowForm] = useState(false);
  const [type, setType] = useState("Screenshot");
  const [name, setName] = useState("");
  const [note, setNote] = useState("");

  const submit = (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    onAdd({ type, name: name.trim(), note: note.trim() });
    setName("");
    setNote("");
    setShowForm(false);
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-sm font-semibold text-gray-700">Evidence ({evidence.length})</p>
        <button
          type="button"
          onClick={() => setShowForm((s) => !s)}
          className="flex items-center gap-1 text-xs font-medium text-primary-600 hover:text-primary-700"
        >
          <PlusIcon className="w-4 h-4" /> Add Evidence
        </button>
      </div>

      {showForm && (
        <form onSubmit={submit} className="border border-gray-200 rounded-lg p-3 space-y-2 bg-gray-50">
          <div className="grid grid-cols-2 gap-2">
            <select
              value={type}
              onChange={(e) => setType(e.target.value)}
              className="border border-gray-200 rounded-lg px-2 py-1.5 text-sm bg-white"
            >
              {EVIDENCE_TYPES.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.id}
                </option>
              ))}
            </select>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="File / attachment name"
              className="border border-gray-200 rounded-lg px-2 py-1.5 text-sm bg-white"
            />
          </div>
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Note (optional)"
            rows={2}
            className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-sm bg-white"
          />
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={() => setShowForm(false)}
              className="text-xs px-3 py-1.5 rounded-lg border border-gray-200 hover:bg-gray-100"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="text-xs px-3 py-1.5 rounded-lg bg-primary-600 text-white hover:bg-primary-700"
            >
              Save
            </button>
          </div>
        </form>
      )}

      <div className="space-y-2">
        {evidence.length === 0 && <p className="text-sm text-gray-400">No evidence attached yet.</p>}
        {evidence.map((item) => {
          const Icon = iconFor(item.type);
          return (
            <div key={item.id} className="flex items-start gap-3 border border-gray-100 rounded-lg p-3 bg-white">
              <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
                <Icon className="w-4 h-4 text-gray-500" />
              </div>
              <div className="min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">{item.name}</p>
                <p className="text-xs text-gray-400">
                  {item.type} • {item.addedAt ? new Date(item.addedAt).toLocaleString() : ""}
                </p>
                {item.note && <p className="text-xs text-gray-600 mt-1">{item.note}</p>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default VaptEvidencePanel;
